// =============================================================
// NotificationBell — polls for database notifications & SLA Alerts
// =============================================================
import { useState, useRef, useEffect } from "react";
import {
  Bell,
  Inbox,
  AlertTriangle,
  Check,
  CheckSquare,
  Sparkles,
  MessageSquare,
  Ticket,
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { fmtRelative } from "../../lib/format";
import {
  api,
  type NotificationItem,
  type Paginated,
  type Ticket as TicketType,
} from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { PriorityBadge } from "../tickets/PriorityBadge";
import { SLACountdown } from "../tickets/SLACountdown";
import { cn } from "../../lib/cn";
import {
  ensureNotificationPermission,
  showOsNotification,
} from "../../lib/osNotify";

export function NotificationBell() {
  const { user, isStaff } = useAuth();
  const { theme } = useTheme();
  const isLight = theme === "light";
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"inbox" | "sla">("inbox");
  const ref = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  // Tracks notification ids we've already surfaced to the OS, so each
  // one fires a desktop notification at most once. Seeded on first
  // load so existing notifications don't all pop at startup.
  const seenNotifIds = useRef<Set<string> | null>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Ask for desktop-notification permission once the user is present.
  useEffect(() => {
    if (user) void ensureNotificationPermission();
  }, [user]);

  // Query 1: SLA Alerts
  const { data: slaData } = useQuery({
    queryKey: ["notifications", user?.id],
    queryFn: async () => {
      const params = {
        slaState: ["at-risk", "breached"],
        status: ["OPEN", "IN_PROGRESS", "ON_HOLD"],
        pageSize: 5,
        ...(isStaff ? {} : { mine: true }),
      };
      const { data } = await api.get<Paginated<TicketType>>("/api/tickets", {
        params,
      });
      return data;
    },
    refetchInterval: 300_000, // 5 minutes (Hostinger DB connection limit protection)
    enabled: !!user,
  });

  // Query 2: Custom Database In-app Notifications
  const { data: dbNotifications } = useQuery({
    queryKey: ["dbNotifications", user?.id],
    queryFn: async () => {
      const { data } = await api.get<{ items: NotificationItem[] }>(
        "/api/notifications",
        { params: { limit: 15 } },
      );
      return data.items;
    },
    refetchInterval: 15_000,
    // Keep polling even when the tab is hidden/minimized, so desktop
    // notifications still fire while the user is on another tab or has
    // the browser minimized. (Browsers throttle hidden-tab timers to
    // ~once per minute, so delivery may lag up to ~60s in that state.)
    refetchIntervalInBackground: true,
    enabled: !!user,
  });

  // Surface newly-arrived notifications as native OS (Windows) toasts.
  useEffect(() => {
    if (!dbNotifications) return;

    // First poll: record what already exists, don't notify for backlog.
    if (seenNotifIds.current === null) {
      seenNotifIds.current = new Set(dbNotifications.map((n) => n.id));
      return;
    }

    const seen = seenNotifIds.current;
    // Newest items come first; reverse so OS toasts appear in order.
    const fresh = dbNotifications
      .filter((n) => !seen.has(n.id))
      .reverse();

    for (const n of fresh) {
      seen.add(n.id);
      if (!n.isRead) {
        void showOsNotification({
          title: n.title,
          body: n.message,
          link: n.link,
          tag: n.id,
        });
      }
    }
  }, [dbNotifications]);

  // Mutation: Mark single notification as read
  const markReadMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.patch(`/api/notifications/${id}/read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dbNotifications", user?.id] });
    },
  });

  // Mutation: Mark all as read
  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      await api.post("/api/notifications/mark-all-read");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dbNotifications", user?.id] });
    },
  });

  const notifications = dbNotifications || [];
  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const slaItems = slaData?.items || [];
  const slaCount = slaItems.length;

  const totalCount = unreadCount + slaCount;

  const handleMarkAllRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (unreadCount > 0) {
      markAllReadMutation.mutate();
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        className="btn-icon relative hover:scale-105 transition-transform duration-200 animate-fade-in"
        onClick={() => setOpen((v) => !v)}
        aria-label="Notifications"
      >
        <Bell className={cn("w-4 h-4 transition-colors", isLight ? "text-slate-600 hover:text-slate-900" : "text-slate-300 hover:text-white")} />
        {totalCount > 0 && (
          <span
            className={cn(
              "absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1",
              "flex items-center justify-center rounded-full",
              "bg-gradient-to-r from-critical-500 to-rose-600 text-white text-[10px] font-bold",
              "animate-pulse shadow-lg shadow-rose-500/30",
              isLight ? "ring-2 ring-white" : "ring-2 ring-[#04061a]"
            )}
          >
            {totalCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-3 w-96 z-40 animate-fade-in shadow-2xl">
          <div className={cn(
            "backdrop-blur-2xl backdrop-saturate-150 rounded-2xl overflow-hidden shadow-2xl",
            isLight
              ? "bg-white/70 border border-slate-200/70 shadow-slate-300/40"
              : "bg-[#090b21]/80 border border-white/15 shadow-indigo-950/40"
          )}>
            {/* Header */}
            <div className={cn(
              "px-4 py-3 border-b flex items-center justify-between",
              isLight ? "bg-slate-50/50 border-slate-100" : "bg-white/5 border-white/5"
            )}>
              <div className={cn("text-sm font-bold flex items-center gap-1.5", isLight ? "text-slate-800" : "text-white")}>
                <Sparkles className={cn("w-4 h-4", isLight ? "text-indigo-600" : "text-indigo-400")} />
                <span>Notification Center</span>
              </div>
              {activeTab === "inbox" && unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  disabled={markAllReadMutation.isPending}
                  className={cn(
                    "text-[11px] font-medium flex items-center gap-1 transition-colors hover:underline",
                    isLight ? "text-indigo-600 hover:text-indigo-700" : "text-indigo-400 hover:text-indigo-300"
                  )}
                >
                  <CheckSquare className="w-3.5 h-3.5" />
                  Clear all unread
                </button>
              )}
            </div>

            {/* Tab Bar */}
            <div className={cn(
              "flex border-b px-2 py-1",
              isLight ? "bg-slate-50/50 border-slate-100" : "bg-white/5 border-white/5"
            )}>
              <button
                onClick={() => setActiveTab("inbox")}
                className={cn(
                  "flex-1 py-2 px-3 text-xs font-semibold rounded-lg transition-all duration-200 flex items-center justify-center gap-1.5",
                  activeTab === "inbox"
                    ? (isLight
                        ? "bg-white text-indigo-600 shadow-sm border border-slate-200/80 font-bold"
                        : "bg-white/10 text-white shadow-sm border border-white/5")
                    : (isLight
                        ? "text-slate-600 hover:text-slate-900 hover:bg-slate-100/50"
                        : "text-slate-400 hover:text-white hover:bg-white/5"),
                )}
              >
                <Inbox className="w-3.5 h-3.5" />
                <span>Inbox</span>
                {unreadCount > 0 && (
                  <span className="bg-indigo-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                    {unreadCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab("sla")}
                className={cn(
                  "flex-1 py-2 px-3 text-xs font-semibold rounded-lg transition-all duration-200 flex items-center justify-center gap-1.5",
                  activeTab === "sla"
                    ? (isLight
                        ? "bg-white text-rose-600 shadow-sm border border-slate-200/80 font-bold"
                        : "bg-white/10 text-white shadow-sm border border-white/5")
                    : (isLight
                        ? "text-slate-600 hover:text-slate-900 hover:bg-slate-100/50"
                        : "text-slate-400 hover:text-white hover:bg-white/5"),
                )}
              >
                <AlertTriangle className="w-3.5 h-3.5" />
                <span>SLA Alerts</span>
                {slaCount > 0 && (
                  <span className="bg-rose-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                    {slaCount}
                  </span>
                )}
              </button>
            </div>

            {/* Content list */}
            <div className={cn(
              "max-h-96 overflow-y-auto divide-y",
              isLight ? "divide-slate-100" : "divide-white/5"
            )}>
              {activeTab === "inbox" ? (
                notifications.length === 0 ? (
                  <div className="px-4 py-8 text-center text-sm text-slate-400 flex flex-col items-center gap-2">
                    <Inbox className="w-8 h-8 text-slate-400" />
                    <span>Your inbox is completely clear! 🍃</span>
                  </div>
                ) : (
                  notifications.map((n) => {
                    const isNewTicket = n.type === "NEW_TICKET";
                    return (
                      <div
                        key={n.id}
                        className={cn(
                          "p-3.5 transition-colors relative group",
                          n.isRead
                            ? (isLight ? "hover:bg-slate-50" : "hover:bg-white/5")
                            : (isLight ? "bg-indigo-50/40 hover:bg-indigo-50/70" : "bg-indigo-500/5 hover:bg-indigo-500/10"),
                        )}
                      >
                        {/* Glowing unread dot indicator */}
                        {!n.isRead && (
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-indigo-500 rounded-full shadow-lg shadow-indigo-500/80" />
                        )}

                        <div className="flex gap-3 pl-3">
                          <div
                            className={cn(
                              "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5",
                              isNewTicket
                                ? (isLight ? "bg-indigo-100 text-indigo-600" : "bg-indigo-500/20 text-indigo-400")
                                : (isLight ? "bg-slate-100 text-slate-600" : "bg-slate-500/20 text-slate-400"),
                            )}
                          >
                            {isNewTicket ? (
                              <Ticket className="w-4 h-4" />
                            ) : (
                              <MessageSquare className="w-4 h-4" />
                            )}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-1.5">
                              <span
                                className={cn(
                                  "text-sm truncate block",
                                  n.isRead
                                    ? (isLight ? "text-slate-600" : "text-slate-200")
                                    : (isLight ? "text-slate-900 font-semibold" : "text-white font-semibold"),
                                )}
                              >
                                {n.title}
                              </span>
                              {!n.isRead && (
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    markReadMutation.mutate(n.id);
                                  }}
                                  title="Mark as read"
                                  className={cn(
                                    "opacity-0 group-hover:opacity-100 transition-opacity p-0.5",
                                    isLight ? "text-indigo-600 hover:text-indigo-700" : "text-indigo-400 hover:text-indigo-300"
                                  )}
                                >
                                  <Check className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                            <p className={cn(
                              "text-xs mt-1 leading-relaxed line-clamp-2",
                              isLight ? "text-slate-600" : "text-slate-400"
                            )}>
                              {n.message}
                            </p>
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-[10px] text-slate-500">
                              {fmtRelative(n.createdAt)}
                              </span>
                              {n.link && (
                                <Link
                                  to={n.link}
                                  onClick={() => {
                                    if (!n.isRead) markReadMutation.mutate(n.id);
                                    setOpen(false);
                                  }}
                                  className={cn(
                                    "text-[10px] font-semibold flex items-center gap-0.5 transition-colors",
                                    isLight ? "text-indigo-600 hover:text-indigo-700" : "text-indigo-400 hover:text-indigo-300"
                                  )}
                                >
                                  View details &rarr;
                                </Link>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )
              ) : slaItems.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-slate-400 flex flex-col items-center gap-2">
                  <AlertTriangle className="w-8 h-8 text-emerald-600" />
                  <span>No breached or at-risk SLAs. Good job! 🎉</span>
                </div>
              ) : (
                slaItems.map((t) => (
                  <Link
                    key={t.id}
                    to={`/tickets/${t.id}`}
                    onClick={() => setOpen(false)}
                    className={cn(
                      "block p-3.5 transition-colors",
                      isLight ? "hover:bg-slate-50" : "hover:bg-white/5"
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className={cn(
                          "text-sm font-medium truncate",
                          isLight ? "text-slate-800" : "text-white"
                        )}>{t.title}</div>
                        <div className="mt-1.5 flex items-center gap-2">
                          <PriorityBadge priority={t.priority} />
                          <SLACountdown
                            deadline={t.slaDeadline}
                            priority={t.priority}
                            status={t.status}
                            compact
                          />
                        </div>
                      </div>
                    </div>
                  </Link>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
